// src/pages/workflows/WorkflowInstances.jsx
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  ArrowForward as AdvanceIcon,
  Clear as RejectIcon,
  History as HistoryIcon,
  Add as AddIcon,
} from '@mui/icons-material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import StatusDisplay from '../../components/ui/StatusDisplay';
import ActionsMenu from '../../components/ui/ActionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// Import hooks and stores
import useWorkflowStore from '../../stores/workflowStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import { EntityCard } from '../../design-system/components/data';

// Filter configuration
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'inProgress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'cancelled', label: 'Cancelled' }
    ]
  },
  {
    name: 'type',
    label: 'Type',
    allLabel: 'All Types',
    options: [
      { value: 'contentApproval', label: 'Content Approval' },
      { value: 'campaignApproval', label: 'Campaign Approval' },
      { value: 'budgetApproval', label: 'Budget Approval' },
      { value: 'custom', label: 'Custom' }
    ]
  },
  {
    name: 'mine',
    label: 'Assigned To',
    allLabel: 'All Workflows',
    options: [
      { value: 'true', label: 'My Workflows' },
      { value: 'pending', label: 'Pending My Approval' }
    ]
  }
];

// Sort options
const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'type', label: 'Type' },
  { field: 'status', label: 'Status' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'updatedAt', label: 'Last Updated' }
];

/**
 * Workflow Instances page component
 */
const WorkflowInstances = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { 
    instances, 
    fetchInstances, 
    cancelWorkflow, 
    isLoading, 
    error,
    meta 
  } = useWorkflowStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission, user } = useAuthStore();
  
  // State for action menus
  const [actionMenus, setActionMenus] = useState({});
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Check permissions
  const canCreate = hasPermission('workflows', 'create');
  const canUpdate = hasPermission('workflows', 'update');
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Handle menu open/close
  const handleOpenMenu = useCallback((e, instanceId) => {
    e.stopPropagation();
    setActionMenus(prev => ({
      ...prev,
      [instanceId]: e.currentTarget
    }));
  }, []);
  
  const handleCloseMenu = useCallback((instanceId) => {
    setActionMenus(prev => ({
      ...prev,
      [instanceId]: null
    }));
  }, []);
  
  // Handle workflow cancellation with confirmation
  const handleCancelWorkflow = useCallback(async (instance) => {
    handleCloseMenu(instance._id);
    
    const confirmed = await confirm({
      title: `Cancel Workflow`,
      content: `Are you sure you want to cancel the workflow "${instance.name}"? This action cannot be undone.`,
      confirmText: 'Cancel Workflow',
      cancelText: 'Keep Workflow',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await cancelWorkflow(instance._id);
        showSuccess(`Workflow "${instance.name}" cancelled successfully`);
      } catch (error) {
        showError(`Failed to cancel workflow: ${error.message}`);
      }
    }
  }, [confirm, cancelWorkflow, showSuccess, showError]);

  // Entity renderer for grid view
  const renderWorkflow = useCallback((instance) => {
    // Safety check
    if (!instance || !instance._id) return null;
    
    // Current step display
    const currentStepDisplay = instance.currentStep 
      ? `${instance.currentStep.name} (${instance.currentStep.assignedTo?.name || 'Unassigned'})`
      : 'N/A';
    
    return (
      <>
        <EntityCard
          title={instance.name}
          description={`${instance.entityType}: ${instance.entityName}`}
          status={
            <StatusDisplay
              type="status"
              value={instance.status}
              size="small"
            />
          }
          chips={[
            <Chip 
              key="type" 
              label={instance.type} 
              size="small" 
              variant="outlined" 
            />
          ]}
          stats={[
            { value: formatDate(instance.createdAt), label: 'Started' },
            { value: currentStepDisplay, label: 'Current Step' }
          ]}
          linkTo={`/workflows/instances/${instance._id}`}
          menuButtonProps={{
            onClick: (e) => handleOpenMenu(e, instance._id)
          }}
        />
        
        {/* Separate actions menu with proper anchoring */}
        <ActionsMenu
          anchorEl={actionMenus[instance._id]}
          open={Boolean(actionMenus[instance._id])}
          onClose={() => handleCloseMenu(instance._id)}
          actions={[
            {
              label: 'View Details',
              onClick: () => navigate(`/workflows/instances/${instance._id}`),
            },
            // Show Advance option if workflow is pending and current user is assigned
            ...(instance.status === 'inProgress' && 
              instance.currentStep?.assignedTo?._id === user?._id ? [
              {
                label: 'Approve Step',
                onClick: () => navigate(`/workflows/instances/${instance._id}/advance`),
              }
            ] : []),
            // Show Reject option if workflow is pending and current user is assigned
            ...(instance.status === 'inProgress' && 
              instance.currentStep?.assignedTo?._id === user?._id ? [
              {
                label: 'Reject Step',
                onClick: () => navigate(`/workflows/instances/${instance._id}/reject`),
              }
            ] : [])
          ]}
          dangerActions={
            canUpdate && !['completed', 'rejected', 'cancelled'].includes(instance.status) ? [
              {
                label: 'Cancel Workflow', 
                onClick: () => handleCancelWorkflow(instance)
              }
            ] : []
          }
        />
      </>
    );
  }, [user, canUpdate, navigate, handleOpenMenu, handleCloseMenu, handleCancelWorkflow, actionMenus]);

  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Workflow Name',
      renderCell: (instance) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {instance.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {instance.entityType}: {instance.entityName}
          </Typography>
        </Box>
      )
    },
    { 
      id: 'type', 
      label: 'Type',
      align: 'center',
      renderCell: (instance) => (
        <Chip
          label={instance.type}
          size="small"
          variant="outlined"
        />
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (instance) => (
        <StatusDisplay
          type="status"
          value={instance.status}
          size="small"
        />
      )
    },
    { 
      id: 'currentStep', 
      label: 'Current Step',
      renderCell: (instance) => (
        instance.currentStep ? (
          <Box>
            <Typography variant="body2">{instance.currentStep.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              Assigned to: {instance.currentStep.assignedTo?.name || 'N/A'}
            </Typography>
          </Box>
        ) : (
          'N/A'
        )
      )
    },
    { 
      id: 'dates', 
      label: 'Dates',
      renderCell: (instance) => (
        <Box>
          <Typography variant="caption" display="block" color="textSecondary">
            Created: {formatDate(instance.createdAt)}
          </Typography>
          <Typography variant="caption" display="block" color="textSecondary">
            Updated: {formatDate(instance.updatedAt)}
          </Typography>
        </Box>
      )
    }
  ];

  // Configure row actions for table view
  const getRowActions = useCallback((instance) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="View Details">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/workflows/instances/${instance._id}`);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {/* Show Advance option if workflow is pending and current user is assigned */}
        {instance.status === 'inProgress' && 
          instance.currentStep?.assignedTo?._id === user?._id && (
          <Tooltip title="Approve Step">
            <IconButton 
              size="small" 
              color="success"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/workflows/instances/${instance._id}/advance`);
              }}
            >
              <AdvanceIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Show Reject option if workflow is pending and current user is assigned */}
        {instance.status === 'inProgress' && 
          instance.currentStep?.assignedTo?._id === user?._id && (
          <Tooltip title="Reject Step">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/workflows/instances/${instance._id}/reject`);
              }}
            >
              <RejectIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {/* Show Cancel option if workflow is not completed, rejected or cancelled */}
        {canUpdate && 
          !['completed', 'rejected', 'cancelled'].includes(instance.status) && (
          <Tooltip title="Cancel Workflow">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelWorkflow(instance);
              }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [user, canUpdate, navigate, handleCancelWorkflow]);
  
  return (
    <>
      <EntityListContainer
        title="Workflows"
        description="Track and manage active approval workflows"
        fetchData={fetchInstances}
        renderEntity={renderWorkflow}
        initialFilters={{
          status: 'all',
          type: 'all',
          mine: 'all'
        }}
        initialSort={{ field: 'updatedAt', direction: 'desc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="table"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (instance) => navigate(`/workflows/instances/${instance._id}`),
          renderRowActions: getRowActions
        }}
        
        // Use direct store values
        entities={instances}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: '/workflows/instances/create',
          createLabel: 'Start New Workflow'
        }}
        emptyState={{
          title: 'No Workflow Instances',
          description: 'Start a new workflow to track your approval processes.',
          icon: <HistoryIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Workflows',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default WorkflowInstances;